import { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '@/services/api';

export interface SystemSettings {
  serviceProtocol: string;
  mainDomain: string;
  serviceBasePath: string;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;
  smtpFromEmail: string;
  smtpFromName: string;
}

export const useSystemSettings = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState<SystemSettings>({
    serviceProtocol: 'http',
    mainDomain: 'localhost',
    serviceBasePath: '',
    smtpHost: 'smtp.gmail.com',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    smtpFromEmail: '',
    smtpFromName: '젠아이랩스 Auth Server',
  });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [
        protocolSetting,
        domainSetting,
        basePathSetting,
        smtpHostSetting,
        smtpPortSetting,
        smtpUserSetting,
        smtpPasswordSetting,
        smtpFromEmailSetting,
        smtpFromNameSetting,
      ] = await Promise.allSettled([
        adminAPI.getSystemSetting('service_protocol'),
        adminAPI.getSystemSetting('main_domain'),
        adminAPI.getSystemSetting('service_base_path'),
        adminAPI.getSystemSetting('smtp_host'),
        adminAPI.getSystemSetting('smtp_port'),
        adminAPI.getSystemSetting('smtp_user'),
        adminAPI.getSystemSetting('smtp_password'),
        adminAPI.getSystemSetting('smtp_from_email'),
        adminAPI.getSystemSetting('smtp_from_name'),
      ]);

      setSettings({
        serviceProtocol: protocolSetting.status === 'fulfilled' ? (protocolSetting.value.setting_value || 'http') : 'http',
        mainDomain: domainSetting.status === 'fulfilled' ? (domainSetting.value.setting_value || 'localhost') : 'localhost',
        serviceBasePath: basePathSetting.status === 'fulfilled' ? (basePathSetting.value.setting_value || '') : '',
        smtpHost: smtpHostSetting.status === 'fulfilled' ? (smtpHostSetting.value.setting_value || 'smtp.gmail.com') : 'smtp.gmail.com',
        smtpPort: smtpPortSetting.status === 'fulfilled' ? (smtpPortSetting.value.setting_value || '587') : '587',
        smtpUser: smtpUserSetting.status === 'fulfilled' ? (smtpUserSetting.value.setting_value || '') : '',
        smtpPassword: smtpPasswordSetting.status === 'fulfilled' ? (smtpPasswordSetting.value.setting_value || '') : '',
        smtpFromEmail: smtpFromEmailSetting.status === 'fulfilled' ? (smtpFromEmailSetting.value.setting_value || '') : '',
        smtpFromName: smtpFromNameSetting.status === 'fulfilled' ? (smtpFromNameSetting.value.setting_value || '젠아이랩스 Auth Server') : '젠아이랩스 Auth Server',
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || '설정을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateSetting = useCallback(async (key: string, value: string) => {
    try {
      await adminAPI.updateSystemSetting(key, value);
      setSettings(prev => ({ ...prev, [key]: value }));
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || '설정 저장에 실패했습니다.');
    }
  }, []);

  const saveDomainSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        updateSetting('service_protocol', settings.serviceProtocol),
        updateSetting('main_domain', settings.mainDomain),
        updateSetting('service_base_path', settings.serviceBasePath),
      ]);
      return '도메인 설정이 저장되었습니다. 서비스 URL이 자동으로 업데이트됩니다.';
    } catch (err: any) {
      setError(err.message || '도메인 설정 저장에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [settings, updateSetting]);

  const saveSmtpSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        updateSetting('smtp_host', settings.smtpHost),
        updateSetting('smtp_port', settings.smtpPort),
        updateSetting('smtp_user', settings.smtpUser),
        updateSetting('smtp_password', settings.smtpPassword),
        updateSetting('smtp_from_email', settings.smtpFromEmail),
        updateSetting('smtp_from_name', settings.smtpFromName),
      ]);
      return 'Gmail SMTP 설정이 저장되었습니다.';
    } catch (err: any) {
      setError(err.message || 'Gmail SMTP 설정 저장에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [settings, updateSetting]);

  return {
    settings,
    setSettings,
    loading,
    error,
    setError,
    loadSettings,
    saveDomainSettings,
    saveSmtpSettings,
  };
};

